import { RefObject, useRef, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { PlaybackStatus } from '../../lib/types';
import ChatPanel from '../ChatPanel';
import StreamerInfoTabs from '../StreamerInfoTabs';
import VideoPlayer, { VideoPlayerHandle } from '../VideoPlayer';
import SubtitleOverlay from '../SubtitleOverlay';
import type { SubtitleData } from '../../hooks/useSubtitles';
import { AnalyticsLogger } from '../../lib/analytics';

interface PeekRoomViewProps {
    streamKey: string | null;
    streamerId: string;
    videoRef: RefObject<HTMLVideoElement | null>;
    status: PlaybackStatus;
    broadcastStatus?: 'live' | 'paused' | 'suspended';
    suspendedCountdown?: number | null;
    partyCount: number;
    peekCount: number;
    viewerName: string;
    pointsError: string | null;
    onPurchasePoints: () => void;
    onEnterLiveRoom: () => void;
    onPointsUpdate: () => void;
    pointsPurchaseCount: number;
    canReport?: boolean;
    scheduleId?: string; // イベントスケジュールID（UUID、チャット履歴のフィルタリング用）
    canModerateChat?: boolean; // 運営スタッフの視聴時に公開チャットの削除を許可
    // Subtitle props
    currentSubtitle?: SubtitleData | null;
    subtitleHistory?: SubtitleData[];
    subtitlesEnabled?: boolean;
    onToggleSubtitles?: () => void;
    subtitleError?: string | null;
    isSubtitleConnecting?: boolean;
}

export default function PeekRoomView({
    streamKey,
    streamerId,
    videoRef,
    status: _status, // Reserved for future use
    broadcastStatus = 'live',
    suspendedCountdown = null,
    partyCount,
    peekCount,
    viewerName,
    pointsError,
    onPurchasePoints,
    onEnterLiveRoom,
    onPointsUpdate,
    pointsPurchaseCount,
    canReport = false,
    scheduleId,
    canModerateChat = false,
    // Subtitle props
    currentSubtitle,
    subtitleHistory = [],
    subtitlesEnabled = false,
    onToggleSubtitles,
    subtitleError,
    isSubtitleConnecting = false,
}: PeekRoomViewProps) {
    const { t } = useTranslation('watch');
    const playerRef = useRef<VideoPlayerHandle>(null);
    const fullscreenContainerRef = useRef<HTMLDivElement>(null);
    // Start muted to comply with browser autoplay policy
    const [isMuted, setIsMuted] = useState(true);
    const [isFullscreen, setIsFullscreen] = useState(false);

    // Handle fullscreen toggle - use the container that includes subtitles
    const toggleFullscreen = useCallback(async () => {
        if (!fullscreenContainerRef.current) return;

        try {
            if (!document.fullscreenElement) {
                await fullscreenContainerRef.current.requestFullscreen();
                // 分析イベント: フルスクリーン開始を追跡
                AnalyticsLogger.getInstance().trackPlayerAction('fullscreen', streamKey || streamerId);
            } else {
                await document.exitFullscreen();
            }
        } catch (error) {
            console.error('Fullscreen error:', error);
        }
    }, [streamKey, streamerId]);

    // Listen for fullscreen changes (including ESC key exit)
    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
        };
    }, []);

    return (
        <>
            {/* Video and Chat Panel */}
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-4 mb-4">
                {/* Video Section */}
                <div>
                    <div className={`bg-black ${isFullscreen ? 'fixed inset-0 z-50' : ''}`}>
                        {/* Room Status Bar - hidden in fullscreen */}
                        {!isFullscreen && (
                            <div className="bg-black text-white flex items-stretch justify-between pr-4 text-sm">
                                <div className="bg-purple-500 text-white px-3 py-2 font-semibold flex items-center">
                                    {t('peekRoom.title')}
                                </div>
                                <div className="flex gap-6 text-white items-center">
                                    <div className="flex items-center gap-2">
                                        <span>💬</span>
                                        <span>{t('peekRoom.chatting')}: <strong>{t('peekRoom.people', { count: partyCount })}</strong></span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span>👀</span>
                                        <span>{t('peekRoom.peeking')}: <strong>{t('peekRoom.people', { count: peekCount })}</strong></span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Video Player Container - this is what goes fullscreen */}
                        <div
                            ref={fullscreenContainerRef}
                            className="relative bg-black"
                        >
                            {/* Video Player */}
                            <div className={`relative ${isFullscreen ? 'w-screen h-screen' : ''}`}>
                                <VideoPlayer
                                    ref={playerRef}
                                    videoRef={videoRef}
                                    showPointsExhausted={!!pointsError}
                                    isBroadcastPaused={broadcastStatus === 'paused'}
                                    isBroadcastSuspended={broadcastStatus === 'suspended'}
                                    hideControls={true}
                                    onMuteChange={setIsMuted}
                                    onFullscreenChange={setIsFullscreen}
                                />

                                {/* Subtitle Overlay - inside player container */}
                                {subtitlesEnabled && broadcastStatus === 'live' && (
                                    <SubtitleOverlay
                                        subtitle={currentSubtitle ?? null}
                                        history={subtitleHistory}
                                        visible={subtitlesEnabled}
                                        showTranslation={true}
                                        showHistory={true}
                                        historyLines={1}
                                        error={subtitleError}
                                        isConnecting={isSubtitleConnecting}
                                        className={isFullscreen ? 'mb-12' : ''}
                                    />
                                )}

                                {/* Broadcast Paused Overlay */}
                                {broadcastStatus === 'paused' && (
                                    <div className="absolute inset-0 bg-black flex items-center justify-center">
                                        <div className="text-center text-white">
                                            <div className="text-6xl mb-4">⏸️</div>
                                            <div className="text-2xl font-bold mb-2">{t('videoPlayer.broadcastPaused')}</div>
                                            <p className="text-gray-300">{t('videoPlayer.broadcastPausedMessage')}</p>
                                        </div>
                                    </div>
                                )}
                                {/* Broadcast Stopped Overlay */}
                                {broadcastStatus === 'suspended' && (
                                    <div className="absolute inset-0 bg-black/90 flex items-center justify-center">
                                        <div className="text-center text-white">
                                            <div className="text-6xl mb-4">🛑</div>
                                            <div className="text-2xl font-bold mb-4">{t('videoPlayer.broadcastSuspended')}</div>
                                            {t('videoPlayer.broadcastSuspendedMessage') && (
                                                <p className="text-gray-300 mb-4">{t('videoPlayer.broadcastSuspendedMessage')}</p>
                                            )}
                                            {suspendedCountdown !== null && (
                                                <div className="bg-yellow-500/20 border border-yellow-500 rounded-lg px-6 py-3 inline-block">
                                                    <span className="text-yellow-400 font-semibold">
                                                        {t('videoPlayer.autoExitIn', { seconds: suspendedCountdown })}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                            </div>

                            {/* Video Controls Bar - inside fullscreen container */}
                            <div className={`bg-gray-900 px-4 py-2 flex items-center justify-end gap-2 ${isFullscreen ? 'absolute bottom-0 left-0 right-0 z-20' : ''}`}>
                                {/* Subtitle Toggle Button */}
                                {onToggleSubtitles && (
                                    <button
                                        onClick={onToggleSubtitles}
                                        className={`px-3 py-1.5 rounded text-sm font-medium transition-all ${
                                            subtitlesEnabled
                                                ? 'bg-purple-500 text-white hover:bg-purple-600'
                                                : 'bg-gray-700 text-gray-300 hover:bg-gray-600 hover:text-white'
                                        }`}
                                        title={subtitlesEnabled ? 'Hide subtitles' : 'Show subtitles'}
                                    >
                                        {subtitlesEnabled ? '📝 Subtitles ON' : '📝 Subtitles OFF'}
                                    </button>
                                )}

                                {/* Mute/Unmute Button */}
                                <button
                                    onClick={() => {
                                        playerRef.current?.toggleMute();
                                        // 分析イベント: ミュート/アンミュートを追跡
                                        const action = isMuted ? 'unmute' : 'mute';
                                        AnalyticsLogger.getInstance().trackPlayerAction(action, streamKey || streamerId);
                                    }}
                                    className="bg-gray-700 hover:bg-gray-600 text-white w-9 h-9 flex items-center justify-center rounded transition-colors"
                                    title={isMuted ? t('videoPlayer.muteOn') : t('videoPlayer.muteOff')}
                                >
                                    {isMuted ? (
                                        <svg width="18" height="18" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM12.293 7.293a1 1 0 011.414 0L15 8.586l1.293-1.293a1 1 0 111.414 1.414L16.414 10l1.293 1.293a1 1 0 01-1.414 1.414L15 11.414l-1.293 1.293a1 1 0 01-1.414-1.414L13.586 10l-1.293-1.293a1 1 0 010-1.414z" clipRule="evenodd" />
                                        </svg>
                                    ) : (
                                        <svg width="18" height="18" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM14.657 2.929a1 1 0 011.414 0A9.972 9.972 0 0119 10a9.972 9.972 0 01-2.929 7.071 1 1 0 01-1.414-1.414A7.971 7.971 0 0017 10c0-2.21-.894-4.208-2.343-5.657a1 1 0 010-1.414zm-2.829 2.828a1 1 0 011.415 0A5.983 5.983 0 0115 10a5.984 5.984 0 01-1.757 4.243 1 1 0 01-1.415-1.415A3.984 3.984 0 0013 10a3.983 3.983 0 00-1.172-2.828 1 1 0 010-1.415z" clipRule="evenodd" />
                                        </svg>
                                    )}
                                </button>

                                {/* Fullscreen Button */}
                                <button
                                    onClick={toggleFullscreen}
                                    className="bg-gray-700 hover:bg-gray-600 text-white w-9 h-9 flex items-center justify-center rounded transition-colors"
                                    title={isFullscreen ? t('videoPlayer.exitFullscreen') : t('videoPlayer.enterFullscreen')}
                                >
                                    {isFullscreen ? (
                                        <svg width="18" height="18" fill="currentColor" viewBox="0 0 20 20">
                                            <path d="M3.28 2.22a.75.75 0 00-1.06 1.06L5.44 6.5H3.75a.75.75 0 000 1.5h4.5a.75.75 0 00.75-.75v-4.5a.75.75 0 00-1.5 0v1.69L4.22 1.16a.75.75 0 00-1.06 0l.12.06zM12.75 6.5a.75.75 0 000 1.5h4.5a.75.75 0 00.75-.75v-4.5a.75.75 0 00-1.5 0v1.69l-3.22-3.22a.75.75 0 10-1.06 1.06l3.22 3.22h-1.69zM3.75 12a.75.75 0 00-.75.75v4.5c0 .414.336.75.75.75h4.5a.75.75 0 000-1.5H6.56l3.22-3.22a.75.75 0 10-1.06-1.06L5.5 15.44v-1.69a.75.75 0 00-.75-.75zm9-.75a.75.75 0 011.06 0l3.22 3.22v-1.69a.75.75 0 011.5 0v4.5a.75.75 0 01-.75.75h-4.5a.75.75 0 010-1.5h1.69l-3.22-3.22a.75.75 0 010-1.06z" />
                                        </svg>
                                    ) : (
                                        <svg width="18" height="18" fill="currentColor" viewBox="0 0 20 20">
                                            <path d="M3 4a1 1 0 011-1h4a1 1 0 010 2H6.414l2.293 2.293a1 1 0 01-1.414 1.414L5 6.414V8a1 1 0 01-2 0V4zm9 1a1 1 0 010-2h4a1 1 0 011 1v4a1 1 0 01-2 0V6.414l-2.293 2.293a1 1 0 11-1.414-1.414L13.586 5H12zm-9 7a1 1 0 012 0v1.586l2.293-2.293a1 1 0 011.414 1.414L6.414 15H8a1 1 0 010 2H4a1 1 0 01-1-1v-4zm13-1a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 010-2h1.586l-2.293-2.293a1 1 0 011.414-1.414L15 13.586V12a1 1 0 011-1z" />
                                        </svg>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Chat Panel */}
                <div className="relative min-h-[400px] lg:min-h-0">
                    <div className="lg:absolute lg:inset-0 flex flex-col h-full">
                        {/* Enter Live Room Button - Top */}
                        <button
                            onClick={onEnterLiveRoom}
                            className="flex-none w-full py-4 px-4 bg-pink-500 hover:bg-pink-600 text-white transition-all flex items-center justify-center gap-2 font-semibold text-base hover:shadow-lg hover:-translate-y-0.5"
                        >
                            <svg width="24" height="24" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M2 5a2 2 0 012-2h7a2 2 0 012 2v4a2 2 0 01-2 2H9l-3 3v-3H4a2 2 0 01-2-2V5z" />
                                <path d="M15 7v2a4 4 0 01-4 4H9.828l-1.766 1.767c.28.149.599.233.938.233h2l3 3v-3h2a2 2 0 002-2V9a2 2 0 00-2-2h-1z" />
                            </svg>
                            {t('peekRoom.enterChatRoom')}
                        </button>

                        <div className="flex-1 min-h-0">
                            <ChatPanel
                                streamKey={streamKey || ''}
                                viewerName={viewerName}
                                chatSendEnabled={false}
                                secretMessageSendEnabled={true}
                                anonymizeUsernames={true}
                                onPointsUpdate={onPointsUpdate}
                                onPurchasePoints={onPurchasePoints}
                                pointsPurchaseCount={pointsPurchaseCount}
                                scheduleId={scheduleId}
                                canModerateChat={canModerateChat}
                            />
                        </div>

                        {/* Points Error */}
                        {pointsError && (
                            <div className="flex-none bg-red-50 border border-red-500 p-4 text-center">
                                <div className="text-red-600 font-semibold">⚠️ {pointsError}</div>
                                <button
                                    onClick={onPurchasePoints}
                                    className="mt-2 px-4 py-2 bg-pink-500 hover:bg-pink-600 text-white font-semibold"
                                >
                                    {t('peekRoom.purchasePoints')}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Profile Information Tabs */}
            <div className="bg-white border border-gray-200 p-6 shadow-sm">
                <StreamerInfoTabs username={streamerId} canReport={canReport} />
            </div>
        </>
    );
}
